import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button, Card, Form, Input, Message, Checkbox, Divider} from 'semantic-ui-react';
import {useNavigate, useParams} from 'react-router-dom';
import {
  API,
  ATTACH_TIER_OPTIONS,
  ATTACH_TIER_TEXT,
  copy,
  getChannelModels,
  normalizeAttachTier,
  showError,
  showInfo,
  showSuccess,
  verifyJSON,
} from '../../helpers';
import {
  BILLING_MODE_TOKEN,
  BILLING_MODE_VIDEO,
  FEATURED_SLOTS,
  VIDEO_INPUT_MODES,
  VIDEO_RESOLUTIONS,
  emptyBillingRate,
  emptyFeaturedEntry,
  isVideoBillingMode,
  normalizeBillingRate,
  normalizeFeaturedEntry,
  usdPerMFromRatio,
} from '../../helpers/billingProfile';
import {CHANNEL_OPTIONS} from '../../constants';
import {renderChannelTip} from '../../helpers/render';

const MODEL_MAPPING_EXAMPLE = {
  'gpt-3.5-turbo-0301': 'gpt-3.5-turbo',
  'gpt-4-0314': 'gpt-4',
  'gpt-4-32k-0314': 'gpt-4-32k',
};

function type2secretPrompt(type, t) {
  switch (type) {
    case 15:
      return t('channel.edit.key_prompts.zhipu');
    case 18:
      return t('channel.edit.key_prompts.spark');
    case 22:
      return t('channel.edit.key_prompts.fastgpt');
    case 23:
      return t('channel.edit.key_prompts.tencent');
    default:
      return t('channel.edit.key_prompts.default');
  }
}

const EditChannel = () => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const channelId = params.id;
  const isEdit = channelId !== undefined;
  const [loading, setLoading] = useState(isEdit);
  const handleCancel = () => {
    navigate('/channel');
  };

  const originInputs = {
    name: '',
    type: 1,
    key: '',
    base_url: '',
    other: '',
    model_mapping: '',
    system_prompt: '',
    models: [],
    groups: ['starter'],
  };
  const [batch, setBatch] = useState(false);
  const [inputs, setInputs] = useState(originInputs);
  const [originModelOptions, setOriginModelOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [basicModels, setBasicModels] = useState([]);
  const [fullModels, setFullModels] = useState([]);
  const [customModel, setCustomModel] = useState('');
  const [config, setConfig] = useState({
    region: '',
    sk: '',
    ak: '',
    user_id: '',
    vertex_ai_project_id: '',
    vertex_ai_adc: '',
    vendor_slug: '',
    model_attach_tiers: {},
    model_billing_rates: {},
    model_featured: {},
    model_summaries: {},
  });
  const syncAttachTiersForModels = (modelList) => {
    setConfig((c) => {
      const tiers = { ...(c.model_attach_tiers || {}) };
      modelList.forEach((m) => {
        if (!tiers[m]) {
          tiers[m] = ATTACH_TIER_TEXT;
        }
      });
      Object.keys(tiers).forEach((k) => {
        if (!modelList.includes(k)) {
          delete tiers[k];
        }
      });
      return { ...c, model_attach_tiers: tiers };
    });
  };

  const syncBillingRatesForModels = (modelList) => {
    setConfig((c) => {
      const rates = { ...(c.model_billing_rates || {}) };
      modelList.forEach((m) => {
        if (!rates[m]) {
          rates[m] = emptyBillingRate();
        }
      });
      Object.keys(rates).forEach((k) => {
        if (!modelList.includes(k)) {
          delete rates[k];
        }
      });
      return { ...c, model_billing_rates: rates };
    });
  };

  const syncFeaturedForModels = (modelList) => {
    setConfig((c) => {
      const featured = { ...(c.model_featured || {}) };
      modelList.forEach((m) => {
        if (!featured[m]) {
          featured[m] = emptyFeaturedEntry();
        }
      });
      Object.keys(featured).forEach((k) => {
        if (!modelList.includes(k)) {
          delete featured[k];
        }
      });
      return { ...c, model_featured: featured };
    });
  };

  const syncModelSummariesForModels = (modelList) => {
    setConfig((c) => {
      const summaries = { ...(c.model_summaries || {}) };
      modelList.forEach((m) => {
        if (summaries[m] === undefined) {
          summaries[m] = '';
        }
      });
      Object.keys(summaries).forEach((k) => {
        if (!modelList.includes(k)) {
          delete summaries[k];
        }
      });
      return { ...c, model_summaries: summaries };
    });
  };

  const handleInputChange = (e, { name, value }) => {
    if (name === 'models') {
      const nextModels = Array.isArray(value)
        ? value
        : value == null
          ? []
          : [value];
      setInputs((inputs) => ({ ...inputs, models: nextModels }));
      syncAttachTiersForModels(nextModels);
      syncBillingRatesForModels(nextModels);
      syncFeaturedForModels(nextModels);
      syncModelSummariesForModels(nextModels);
      return;
    }
    setInputs((inputs) => ({ ...inputs, [name]: value }));
    if (name === 'type') {
      let localModels = getChannelModels(value);
      if (inputs.models.length === 0) {
        setInputs((inputs) => ({ ...inputs, models: localModels }));
        syncAttachTiersForModels(localModels);
      }
      setBasicModels(localModels);
    }
  };

  const setModelAttachTier = (modelId, tier) => {
    setConfig((c) => ({
      ...c,
      model_attach_tiers: {
        ...(c.model_attach_tiers || {}),
        [modelId]: normalizeAttachTier(tier),
      },
    }));
  };

  const setModelBillingRate = (modelId, field, value) => {
    setConfig((c) => ({
      ...c,
      model_billing_rates: {
        ...(c.model_billing_rates || {}),
        [modelId]: {
          ...(c.model_billing_rates || {})[modelId],
          [field]: value,
        },
      },
    }));
  };

  const setModelVideoTier = (modelId, resolution, value) => {
    setConfig((c) => {
      const cur = normalizeBillingRate((c.model_billing_rates || {})[modelId]);
      return {
        ...c,
        model_billing_rates: {
          ...(c.model_billing_rates || {}),
          [modelId]: {
            ...cur,
            resolution_tiers: {
              ...cur.resolution_tiers,
              [resolution]: value,
            },
          },
        },
      };
    });
  };

  const setModelFeatured = (modelId, field, value) => {
    setConfig((c) => ({
      ...c,
      model_featured: {
        ...(c.model_featured || {}),
        [modelId]: {
          ...normalizeFeaturedEntry((c.model_featured || {})[modelId]),
          [field]: value,
        },
      },
    }));
  };

  const setModelSummary = (modelId, value) => {
    setConfig((c) => ({
      ...c,
      model_summaries: {
        ...(c.model_summaries || {}),
        [modelId]: value,
      },
    }));
  };

  const toggleVideoBilling = (modelId, checked) => {
    setConfig((c) => {
      const cur = normalizeBillingRate((c.model_billing_rates || {})[modelId]);
      return {
        ...c,
        model_billing_rates: {
          ...(c.model_billing_rates || {}),
          [modelId]: {
            ...cur,
            billing_mode: checked ? BILLING_MODE_VIDEO : BILLING_MODE_TOKEN,
            completion_ratio: checked ? 1 : cur.completion_ratio,
          },
        },
      };
    });
  };

  const fetchGlobalRatios = async () => {
    try {
      const res = await API.get('/api/option/');
      const { success, data } = res.data || {};
      if (!success || !Array.isArray(data)) return { model: {}, completion: {} };
      let model = {};
      let completion = {};
      data.forEach((item) => {
        if (item.key === 'ModelRatio' && item.value) {
          try {
            model = JSON.parse(item.value);
          } catch {
            model = {};
          }
        }
        if (item.key === 'CompletionRatio' && item.value) {
          try {
            completion = JSON.parse(item.value);
          } catch {
            completion = {};
          }
        }
      });
      return { model, completion };
    } catch {
      return { model: {}, completion: {} };
    }
  };

  const handleConfigChange = (e, { name, value }) => {
    setConfig((inputs) => ({ ...inputs, [name]: value }));
  };

  const loadChannel = async () => {
    let res = await API.get(`/api/channel/${channelId}`);
    const { success, message, data } = res.data;
    if (success) {
      if (data.models === '') {
        data.models = [];
      } else {
        data.models = data.models.split(',');
      }
      if (data.group === '') {
        data.groups = [];
      } else {
        data.groups = data.group.split(',').map((g) => {
          const x = String(g).trim().toLowerCase();
          if (x === 'free' || x === 'default' || x === '') return 'starter';
          return g.trim();
        });
      }
      if (data.model_mapping !== '') {
        data.model_mapping = JSON.stringify(
          JSON.parse(data.model_mapping),
          null,
          2
        );
      }
      setInputs(data);
      if (data.config !== '') {
        const parsed = JSON.parse(data.config);
        if (!parsed.model_attach_tiers) {
          parsed.model_attach_tiers = {};
        }
        if (!parsed.model_billing_rates) {
          parsed.model_billing_rates = {};
        }
        if (!parsed.model_featured) {
          parsed.model_featured = {};
        }
        if (!parsed.model_summaries) {
          parsed.model_summaries = {};
        }
        const globals = await fetchGlobalRatios();
        data.models.forEach((m) => {
          if (!parsed.model_billing_rates[m]) {
            parsed.model_billing_rates[m] = {
              ...emptyBillingRate(),
              model_ratio: globals.model[m] ?? '',
              completion_ratio: globals.completion[m] ?? '',
            };
          } else {
            parsed.model_billing_rates[m] = normalizeBillingRate({
              ...parsed.model_billing_rates[m],
              model_ratio:
                parsed.model_billing_rates[m].model_ratio ?? globals.model[m] ?? '',
              completion_ratio:
                parsed.model_billing_rates[m].completion_ratio ??
                globals.completion[m] ??
                '',
            });
          }
          if (!parsed.model_featured[m]) {
            parsed.model_featured[m] = emptyFeaturedEntry();
          } else {
            parsed.model_featured[m] = normalizeFeaturedEntry(parsed.model_featured[m]);
          }
          if (parsed.model_summaries[m] === undefined) {
            parsed.model_summaries[m] = '';
          }
        });
        setConfig(parsed);
      } else {
        const globals = await fetchGlobalRatios();
        const rates = {};
        const featured = {};
        const summaries = {};
        data.models.forEach((m) => {
          rates[m] = {
            ...emptyBillingRate(),
            model_ratio: globals.model[m] ?? '',
            completion_ratio: globals.completion[m] ?? '',
          };
          featured[m] = emptyFeaturedEntry();
          summaries[m] = '';
        });
        setConfig((c) => ({
          ...c,
          model_billing_rates: rates,
          model_featured: featured,
          model_summaries: summaries,
        }));
      }
      setBasicModels(getChannelModels(data.type));
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const fetchModels = async () => {
    try {
      let res = await API.get(`/api/channel/models`);
      let localModelOptions = res.data.data.map((model) => ({
        key: model.id,
        text: model.id,
        value: model.id,
      }));
      setOriginModelOptions(localModelOptions);
      setFullModels(res.data.data.map((model) => model.id));
    } catch (error) {
      showError(error.message);
    }
  };

  const fetchGroups = async () => {
    try {
      let res = await API.get(`/api/group/`);
      setGroupOptions(
        res.data.data.map((group) => ({
          key: group,
          text: group,
          value: group,
        }))
      );
    } catch (error) {
      showError(error.message);
    }
  };

  useEffect(() => {
    let localModelOptions = [...originModelOptions];
    inputs.models.forEach((model) => {
      if (!localModelOptions.find((option) => option.key === model)) {
        localModelOptions.push({
          key: model,
          text: model,
          value: model,
        });
      }
    });
    setModelOptions(localModelOptions);
  }, [originModelOptions, inputs.models]);

  useEffect(() => {
    if (isEdit) {
      loadChannel().then();
    } else {
      let localModels = getChannelModels(inputs.type);
      setBasicModels(localModels);
    }
    fetchModels().then();
    fetchGroups().then();
  }, []);

  const submit = async () => {
    if (inputs.key === '') {
      if (config.ak !== '' && config.sk !== '' && config.region !== '') {
        inputs.key = `${config.ak}|${config.sk}|${config.region}`;
      } else if (
        config.region !== '' &&
        config.vertex_ai_project_id !== '' &&
        config.vertex_ai_adc !== ''
      ) {
        inputs.key = `${config.region}|${config.vertex_ai_project_id}|${config.vertex_ai_adc}`;
      }
    }
    if (!isEdit && (inputs.name === '' || inputs.key === '')) {
      showInfo(t('channel.edit.messages.name_required'));
      return;
    }
    if (inputs.type !== 43 && inputs.models.length === 0) {
      showInfo(t('channel.edit.messages.models_required'));
      return;
    }
    if (inputs.model_mapping !== '' && !verifyJSON(inputs.model_mapping)) {
      showInfo(t('channel.edit.messages.model_mapping_invalid'));
      return;
    }
    let localInputs = { ...inputs };
    if (localInputs.key === 'undefined|undefined|undefined') {
      localInputs.key = ''; // prevent potential bug
    }
    const billingClean = {};
    Object.entries(config.model_billing_rates || {}).forEach(([modelId, rate]) => {
      const normalized = normalizeBillingRate(rate);
      if (isVideoBillingMode(normalized.billing_mode)) {
        const tiers = {};
        VIDEO_RESOLUTIONS.forEach((res) => {
          const v = parseFloat(normalized.resolution_tiers?.[res]);
          if (v > 0) tiers[res] = v;
        });
        const anchor =
          tiers['480p'] ||
          tiers[Object.keys(tiers).sort()[0]];
        if (modelId && anchor > 0) {
          billingClean[modelId] = {
            billing_mode: BILLING_MODE_VIDEO,
            video_input_mode: normalized.video_input_mode || 'excl_video_ref',
            model_ratio: anchor,
            completion_ratio: 1,
            resolution_tiers: tiers,
          };
        }
      } else {
        const mr = parseFloat(normalized.model_ratio);
        const cr = parseFloat(normalized.completion_ratio);
        if (modelId && mr > 0 && cr > 0) {
          billingClean[modelId] = {
            billing_mode: BILLING_MODE_TOKEN,
            model_ratio: mr,
            completion_ratio: cr,
          };
        }
      }
    });
    const featuredClean = {};
    Object.entries(config.model_featured || {}).forEach(([modelId, feat]) => {
      const f = normalizeFeaturedEntry(feat);
      if (modelId && f.enabled && f.slot >= 1 && f.slot <= 4) {
        featuredClean[modelId] = {
          enabled: true,
          slot: f.slot,
          tagline: (f.tagline || '').trim(),
        };
      }
    });
    const summariesClean = {};
    Object.entries(config.model_summaries || {}).forEach(([modelId, summary]) => {
      const s = String(summary || '').trim();
      if (modelId && s) {
        summariesClean[modelId] = s;
      }
    });
    const configPayload = {
      ...config,
      model_billing_rates: billingClean,
      model_featured: featuredClean,
      model_summaries: summariesClean,
    };
    if (localInputs.base_url && localInputs.base_url.endsWith('/')) {
      localInputs.base_url = localInputs.base_url.slice(
        0,
        localInputs.base_url.length - 1
      );
    }
    if (localInputs.type === 3 && localInputs.other === '') {
      localInputs.other = '2024-03-01-preview';
    }
    let res;
    localInputs.models = localInputs.models.join(',');
    localInputs.group = localInputs.groups.join(',');
    localInputs.config = JSON.stringify(configPayload);
    if (isEdit) {
      res = await API.put(`/api/channel/`, {
        ...localInputs,
        id: parseInt(channelId),
      });
    } else {
      res = await API.post(`/api/channel/`, localInputs);
    }
    const { success, message } = res.data;
    if (success) {
      if (isEdit) {
        showSuccess(t('channel.edit.messages.update_success'));
      } else {
        showSuccess(t('channel.edit.messages.create_success'));
        setInputs(originInputs);
      }
      navigate('/channel');
    } else {
      showError(message);
    }
  };

  const addCustomModel = () => {
    if (customModel.trim() === '') return;
    if (inputs.models.includes(customModel)) return;
    let localModels = [...inputs.models];
    localModels.push(customModel);
    let localModelOptions = [];
    localModelOptions.push({
      key: customModel,
      text: customModel,
      value: customModel,
    });
    setModelOptions((modelOptions) => {
      return [...modelOptions, ...localModelOptions];
    });
    setCustomModel('');
    syncAttachTiersForModels(localModels);
    handleInputChange(null, { name: 'models', value: localModels });
  };

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>
            {isEdit
              ? t('channel.edit.title_edit')
              : t('channel.edit.title_create')}
          </Card.Header>
          <Form loading={loading} autoComplete='new-password'>
            <Form.Field>
              <Form.Select
                label={t('channel.edit.type')}
                name='type'
                required
                search
                options={CHANNEL_OPTIONS}
                value={inputs.type}
                onChange={handleInputChange}
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={t('channel.edit.name')}
                name='name'
                placeholder={t('channel.edit.name_placeholder')}
                onChange={handleInputChange}
                value={inputs.name}
                required
              />
            </Form.Field>
            <Form.Field>
              <Form.Dropdown
                label={t('channel.edit.group')}
                placeholder={t('channel.edit.group_placeholder')}
                name='groups'
                required
                fluid
                multiple
                selection
                allowAdditions
                additionLabel={t('channel.edit.group_addition')}
                onChange={handleInputChange}
                value={inputs.groups}
                autoComplete='new-password'
                options={groupOptions}
              />
            </Form.Field>
            {renderChannelTip(inputs.type)}

            {/* Azure OpenAI specific fields */}
            {inputs.type === 3 && (
              <>
                <Message>
                  Ensure the deployment name matches the model name. The gateway
                  rewrites the request body model field to your deployment
                  target (dots in model names are removed).
                </Message>
                <Form.Field>
                  <Form.Input
                    label={t('channel.edit.azure_endpoint')}
                    name='base_url'
                    placeholder={t('channel.edit.azure_endpoint_placeholder')}
                    onChange={handleInputChange}
                    value={inputs.base_url}
                    autoComplete='new-password'
                  />
                </Form.Field>
                <Form.Field>
                  <Form.Input
                    label={t('channel.edit.azure_api_version')}
                    name='other'
                    placeholder={t('channel.edit.azure_api_version_placeholder')}
                    onChange={handleInputChange}
                    value={inputs.other}
                    autoComplete='new-password'
                  />
                </Form.Field>
              </>
            )}

            {/* Custom base URL field */}
            {inputs.type === 8 && (
              <Form.Field>
                <Form.Input
                    required
                    label={t('channel.edit.proxy_url')}
                    name='base_url'
                    placeholder={t('channel.edit.proxy_url_placeholder')}
                    onChange={handleInputChange}
                    value={inputs.base_url}
                    autoComplete='new-password'
                />
              </Form.Field>
            )}
            {inputs.type === 50 && (
                <Form.Field>
                  <Form.Input
                      required
                  label={t('channel.edit.base_url')}
                  name='base_url'
                  placeholder={t('channel.edit.base_url_placeholder')}
                  onChange={handleInputChange}
                  value={inputs.base_url}
                  autoComplete='new-password'
                />
              </Form.Field>
            )}

            {inputs.type === 18 && (
              <Form.Field>
                <Form.Input
                  label={t('channel.edit.spark_version')}
                  name='other'
                  placeholder={t('channel.edit.spark_version_placeholder')}
                  onChange={handleInputChange}
                  value={inputs.other}
                  autoComplete='new-password'
                />
              </Form.Field>
            )}
            {inputs.type === 21 && (
              <Form.Field>
                <Form.Input
                  label={t('channel.edit.knowledge_id')}
                  name='other'
                  placeholder={t('channel.edit.knowledge_id_placeholder')}
                  onChange={handleInputChange}
                  value={inputs.other}
                  autoComplete='new-password'
                />
              </Form.Field>
            )}
            {inputs.type === 17 && (
              <Form.Field>
                <Form.Input
                  label={t('channel.edit.plugin_param')}
                  name='other'
                  placeholder={t('channel.edit.plugin_param_placeholder')}
                  onChange={handleInputChange}
                  value={inputs.other}
                  autoComplete='new-password'
                />
              </Form.Field>
            )}
            {inputs.type === 34 && (
              <Message>{t('channel.edit.coze_notice')}</Message>
            )}
            {inputs.type === 40 && (
              <Message>
                {t('channel.edit.douban_notice')}
                <a
                  target='_blank'
                  href='https://console.volcengine.com/ark/region:ark+cn-beijing/endpoint'
                >
                  {t('channel.edit.douban_notice_link')}
                </a>
                {t('channel.edit.douban_notice_2')}
              </Message>
            )}
            {inputs.type !== 43 && (
              <Form.Field>
                <Form.Dropdown
                  label={t('channel.edit.models')}
                  placeholder={t('channel.edit.models_placeholder')}
                  name='models'
                  required
                  fluid
                  multiple
                  search
                  onLabelClick={(e, { value }) => {
                    copy(value).then();
                  }}
                  selection
                  onChange={handleInputChange}
                  value={inputs.models}
                  autoComplete='new-password'
                  options={modelOptions}
                />
              </Form.Field>
            )}
            {inputs.type !== 43 && inputs.models.length > 0 && (
              <Form.Field>
                <label>Chat attachment capability (per model)</label>
                <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '0.75rem' }}>
                  Short labels: <strong>Text</strong> (.txt, .md, .csv, .json),{' '}
                  <strong>Doc</strong> (+ .pdf, .docx, .pptx),{' '}
                  <strong>Vision+Doc</strong> (+ images). Shown on token form; does not change model id.
                </p>
                {inputs.models.map((modelId) => (
                  <Form.Select
                    key={modelId}
                    label={modelId}
                    options={ATTACH_TIER_OPTIONS}
                    value={
                      (config.model_attach_tiers || {})[modelId] || ATTACH_TIER_TEXT
                    }
                    onChange={(e, { value }) => setModelAttachTier(modelId, value)}
                  />
                ))}
              </Form.Field>
            )}
            {inputs.type !== 43 && inputs.models.length > 0 && (
              <Form.Field>
                <label>Billing &amp; homepage featured (per model)</label>
                <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '0.75rem' }}>
                  Text models: model ratio + completion ratio. Video models: per-resolution
                  ratios (completion locked at 1). Featured slots (max 4 globally) sync to the
                  homepage. Model highlight copy syncs to model list &amp; token picker. Merges
                  into Settings → Operation on save.
                </p>
                {inputs.models.map((modelId) => {
                  const rate = normalizeBillingRate(
                    (config.model_billing_rates || {})[modelId]
                  );
                  const feat = normalizeFeaturedEntry(
                    (config.model_featured || {})[modelId]
                  );
                  const summary = (config.model_summaries || {})[modelId] || '';
                  const isVideo = isVideoBillingMode(rate.billing_mode);
                  return (
                    <div
                      key={`billing-${modelId}`}
                      style={{
                        marginBottom: '1rem',
                        padding: '12px 14px',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 4,
                        background: 'rgba(0,0,0,0.2)',
                      }}
                    >
                      <div
                        style={{
                          fontFamily: 'JetBrains Mono, Consolas',
                          fontSize: '0.85rem',
                          color: '#00ff85',
                          marginBottom: '8px',
                        }}
                      >
                        {modelId}
                        {isVideo && (
                          <span
                            style={{
                              marginLeft: 8,
                              fontSize: '0.65rem',
                              letterSpacing: '0.12em',
                              color: 'rgba(255,200,100,0.95)',
                            }}
                          >
                            VIDEO
                          </span>
                        )}
                      </div>
                      <Checkbox
                        label='Video generation model (API only — not in Chat)'
                        checked={isVideo}
                        onChange={(e, { checked }) =>
                          toggleVideoBilling(modelId, !!checked)
                        }
                        style={{ marginBottom: 8, display: 'block' }}
                      />
                      <Checkbox
                        label='Homepage featured'
                        checked={feat.enabled}
                        onChange={(e, { checked }) =>
                          setModelFeatured(modelId, 'enabled', !!checked)
                        }
                        style={{ marginBottom: 8, display: 'block' }}
                      />
                      {feat.enabled && (
                        <Form.Group widths='equal'>
                          <Form.Select
                            label='Featured slot (1–4)'
                            options={FEATURED_SLOTS}
                            value={feat.slot}
                            onChange={(e, { value }) =>
                              setModelFeatured(modelId, 'slot', value)
                            }
                          />
                          <Form.Input
                            label='Tagline (homepage card)'
                            value={feat.tagline}
                            placeholder='Short feature description'
                            onChange={(e, { value }) =>
                              setModelFeatured(modelId, 'tagline', value)
                            }
                          />
                        </Form.Group>
                      )}
                      <Form.Input
                        label='Model highlight (model list & token picker)'
                        value={summary}
                        placeholder='Strengths / use case — official or custom; leave empty to use catalog default if any'
                        onChange={(e, { value }) => setModelSummary(modelId, value)}
                        style={{ marginTop: 8 }}
                      />
                      <Divider style={{ margin: '10px 0' }} />
                      {isVideo ? (
                        <>
                          <Form.Select
                            label='Video input pricing mode'
                            options={VIDEO_INPUT_MODES}
                            value={rate.video_input_mode}
                            onChange={(e, { value }) =>
                              setModelBillingRate(modelId, 'video_input_mode', value)
                            }
                          />
                          <p
                            style={{
                              color: 'rgba(255,255,255,0.55)',
                              fontSize: '0.8rem',
                              margin: '0 0 8px',
                            }}
                          >
                            Model ratio per resolution tier · USD/M ≈ 2 × ratio
                          </p>
                          <Form.Group widths='equal'>
                            {VIDEO_RESOLUTIONS.map((res) => {
                              const ratioVal = rate.resolution_tiers?.[res] ?? '';
                              const usd = usdPerMFromRatio(ratioVal);
                              return (
                                <Form.Input
                                  key={res}
                                  label={`${res.toUpperCase()} ratio${usd != null ? ` (~$${usd.toFixed(2)}/M)` : ''}`}
                                  type='number'
                                  step='0.001'
                                  min='0'
                                  value={ratioVal}
                                  onChange={(e, { value }) =>
                                    setModelVideoTier(modelId, res, value)
                                  }
                                />
                              );
                            })}
                          </Form.Group>
                        </>
                      ) : (
                        <Form.Group widths='equal'>
                          <Form.Input
                            label='Model ratio'
                            type='number'
                            step='0.001'
                            min='0'
                            value={rate.model_ratio ?? ''}
                            onChange={(e, { value }) =>
                              setModelBillingRate(modelId, 'model_ratio', value)
                            }
                          />
                          <Form.Input
                            label='Completion ratio'
                            type='number'
                            step='0.01'
                            min='0'
                            value={rate.completion_ratio ?? ''}
                            onChange={(e, { value }) =>
                              setModelBillingRate(modelId, 'completion_ratio', value)
                            }
                          />
                        </Form.Group>
                      )}
                    </div>
                  );
                })}
              </Form.Field>
            )}
            {inputs.type !== 43 && (
              <div style={{ lineHeight: '40px', marginBottom: '12px' }}>
                <Button
                  type={'button'}
                  onClick={() => {
                    handleInputChange(null, {
                      name: 'models',
                      value: basicModels,
                    });
                  }}
                >
                  {t('channel.edit.buttons.fill_models')}
                </Button>
                <Button
                  type={'button'}
                  onClick={() => {
                    handleInputChange(null, {
                      name: 'models',
                      value: fullModels,
                    });
                  }}
                >
                  {t('channel.edit.buttons.fill_all')}
                </Button>
                <Button
                  type={'button'}
                  onClick={() => {
                    handleInputChange(null, { name: 'models', value: [] });
                  }}
                >
                  {t('channel.edit.buttons.clear')}
                </Button>
                <Input
                  action={
                    <Button type={'button'} onClick={addCustomModel}>
                      {t('channel.edit.buttons.add_custom')}
                    </Button>
                  }
                  placeholder={t('channel.edit.buttons.custom_placeholder')}
                  value={customModel}
                  onChange={(e, { value }) => {
                    setCustomModel(value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      addCustomModel();
                      e.preventDefault();
                    }
                  }}
                />
              </div>
            )}
            {inputs.type !== 43 && (
              <>
                <Form.Field>
                  <Form.Input
                    label='Homepage vendor slug (optional)'
                    name='vendor_slug'
                    placeholder='e.g. doubao, deepseek — overrides auto vendor on homepage; leave empty to infer from channel type'
                    onChange={handleConfigChange}
                    value={config.vendor_slug || ''}
                    autoComplete='off'
                  />
                </Form.Field>
                <Form.Field>
                  <Form.TextArea
                    label={t('channel.edit.model_mapping')}
                    placeholder={`${t(
                      'channel.edit.model_mapping_placeholder'
                    )}\n${JSON.stringify(MODEL_MAPPING_EXAMPLE, null, 2)}`}
                    name='model_mapping'
                    onChange={handleInputChange}
                    value={inputs.model_mapping}
                    style={{
                      minHeight: 150,
                      fontFamily: 'JetBrains Mono, Consolas',
                    }}
                    autoComplete='new-password'
                  />
                </Form.Field>
                <Form.Field>
                  <Form.TextArea
                    label={t('channel.edit.system_prompt')}
                    placeholder={t('channel.edit.system_prompt_placeholder')}
                    name='system_prompt'
                    onChange={handleInputChange}
                    value={inputs.system_prompt}
                    style={{
                      minHeight: 150,
                      fontFamily: 'JetBrains Mono, Consolas',
                    }}
                    autoComplete='new-password'
                  />
                </Form.Field>
              </>
            )}
            {inputs.type === 33 && (
              <Form.Field>
                <Form.Input
                  label='Region'
                  name='region'
                  required
                  placeholder={t('channel.edit.aws_region_placeholder')}
                  onChange={handleConfigChange}
                  value={config.region}
                  autoComplete=''
                />
                <Form.Input
                  label='AK'
                  name='ak'
                  required
                  placeholder={t('channel.edit.aws_ak_placeholder')}
                  onChange={handleConfigChange}
                  value={config.ak}
                  autoComplete=''
                />
                <Form.Input
                  label='SK'
                  name='sk'
                  required
                  placeholder={t('channel.edit.aws_sk_placeholder')}
                  onChange={handleConfigChange}
                  value={config.sk}
                  autoComplete=''
                />
              </Form.Field>
            )}
            {inputs.type === 42 && (
              <Form.Field>
                <Form.Input
                  label='Region'
                  name='region'
                  required
                  placeholder={t('channel.edit.vertex_region_placeholder')}
                  onChange={handleConfigChange}
                  value={config.region}
                  autoComplete=''
                />
                <Form.Input
                  label={t('channel.edit.vertex_project_id')}
                  name='vertex_ai_project_id'
                  required
                  placeholder={t('channel.edit.vertex_project_id_placeholder')}
                  onChange={handleConfigChange}
                  value={config.vertex_ai_project_id}
                  autoComplete=''
                />
                <Form.Input
                  label={t('channel.edit.vertex_credentials')}
                  name='vertex_ai_adc'
                  required
                  placeholder={t('channel.edit.vertex_credentials_placeholder')}
                  onChange={handleConfigChange}
                  value={config.vertex_ai_adc}
                  autoComplete=''
                />
              </Form.Field>
            )}
            {inputs.type === 34 && (
              <Form.Input
                label={t('channel.edit.user_id')}
                name='user_id'
                required
                placeholder={t('channel.edit.user_id_placeholder')}
                onChange={handleConfigChange}
                value={config.user_id}
                autoComplete=''
              />
            )}
            {inputs.type !== 33 &&
              inputs.type !== 42 &&
              (batch ? (
                <Form.Field>
                  <Form.TextArea
                    label={t('channel.edit.key')}
                    name='key'
                    required
                    placeholder={t('channel.edit.batch_placeholder')}
                    onChange={handleInputChange}
                    value={inputs.key}
                    style={{
                      minHeight: 150,
                      fontFamily: 'JetBrains Mono, Consolas',
                    }}
                    autoComplete='new-password'
                  />
                </Form.Field>
              ) : (
                <Form.Field>
                  <Form.Input
                    label={t('channel.edit.key')}
                    name='key'
                    required
                    placeholder={type2secretPrompt(inputs.type, t)}
                    onChange={handleInputChange}
                    value={inputs.key}
                    autoComplete='new-password'
                  />
                </Form.Field>
              ))}
            {inputs.type === 37 && (
              <Form.Field>
                <Form.Input
                  label='Account ID'
                  name='user_id'
                  required
                  placeholder={t('channel.edit.cloudflare_account_id_placeholder')}
                  onChange={handleConfigChange}
                  value={config.user_id}
                  autoComplete=''
                />
              </Form.Field>
            )}
            {inputs.type !== 33 && !isEdit && (
              <Form.Checkbox
                checked={batch}
                label={t('channel.edit.batch')}
                name='batch'
                onChange={() => setBatch(!batch)}
              />
            )}
            {inputs.type !== 3 &&
              inputs.type !== 33 &&
              inputs.type !== 8 &&
                inputs.type !== 50 &&
              inputs.type !== 22 && (
                <Form.Field>
                  <Form.Input
                      label={t('channel.edit.proxy_url')}
                    name='base_url'
                      placeholder={t('channel.edit.proxy_url_placeholder')}
                    onChange={handleInputChange}
                    value={inputs.base_url}
                    autoComplete='new-password'
                  />
                </Form.Field>
              )}
            {inputs.type === 22 && (
              <Form.Field>
                <Form.Input
                  label={t('channel.edit.fastgpt_base_url')}
                  name='base_url'
                  placeholder={t('channel.edit.fastgpt_base_url_placeholder')}
                  onChange={handleInputChange}
                  value={inputs.base_url}
                  autoComplete='new-password'
                />
              </Form.Field>
            )}
            <Button onClick={handleCancel}>
              {t('channel.edit.buttons.cancel')}
            </Button>
            <Button
              type={isEdit ? 'button' : 'submit'}
              positive
              onClick={submit}
            >
              {t('channel.edit.buttons.submit')}
            </Button>
          </Form>
        </Card.Content>
      </Card>
    </div>
  );
};

export default EditChannel;
